import { ApiError } from '@/api/errors'
import { http } from '@/api/http'
import { env } from '@/config/env'
import type {
  ChatAttachmentDto,
  ChatListDto,
  ChatMessageDto,
  ChatRoomDto,
  CreateChatBody,
  SendMessageBody,
} from '@/api/types/chat'
import { t } from '@/i18n'

/** Сколько комнат забираем за раз: у клиента их единицы, страниц не листаем. */
const PAGE_SIZE = 50

/**
 * Чат поддержки. Прокси добавляет ключ сервиса и id клиента, поэтому здесь
 * только пути — они повторяют документацию сервиса один в один.
 */
export const chatService = {
  rooms: () =>
    http.get<ChatListDto>('/chat/chats', { filter: 'all', page: 1, pageSize: PAGE_SIZE }),
  room: (roomId: string) => http.get<ChatRoomDto>(`/chat/chats/${roomId}`),
  create: (body: CreateChatBody) => http.post<ChatRoomDto>('/chat/chats', body),
  messages: (roomId: string, limit = PAGE_SIZE) =>
    http.get<ChatMessageDto[]>(`/chat/chats/${roomId}/messages`, { limit }),
  send: (roomId: string, body: SendMessageBody) =>
    http.post<ChatMessageDto>(`/chat/chats/${roomId}/messages`, body),
  /** Помечает прочитанными все сообщения оператора в комнате. */
  markViewed: (roomId: string) => http.patch<void>(`/chat/chats/${roomId}/view`),

  /**
   * Загрузка вложения — единственный запрос чата мимо прокси: тот читает тело
   * как текст и побил бы бинарник. Сервис пускает браузер напрямую (CORS
   * открыт), а ключ и так уже на клиенте ради SignalR.
   */
  async upload(file: File, customerId: string): Promise<ChatAttachmentDto> {
    const form = new FormData()
    form.append('file', file)

    const response = await fetch(`${env.chatUrl}/api/attachments`, {
      method: 'POST',
      headers: { 'X-API-Key': env.chatKey, 'X-Customer-Id': customerId },
      body: form,
    })
    if (!response.ok) throw new ApiError(t('chat.uploadFailed'), response.status)

    return (await response.json()) as ChatAttachmentDto
  },
}
